import constants from '../constants';
import AnnotationUtils from '../annotation/utils';
import { getValue } from '../store/state/state-manager';
import { C11nEnv, createC11nEnv } from '../interpreter/c11n-env';

import { CONTAINER_NAMES, getActiveContainerItemName, getContainerItemData } from './container-utils';
import type { Action, Assignment, CaseItem } from './types';

class FlowContainer {
  static createContainerPConnect(
    flowContainerInfo: { accessedOrder: any; items: any },
    pageReference: string,
    containerName: string,
    isAssignmentView: boolean = false
  ) {
    const { accessedOrder, items } = flowContainerInfo;

    if (accessedOrder && items) {
      const activeContainerItemKey = accessedOrder[accessedOrder.length - 1];

      if (items[activeContainerItemKey]?.view && Object.keys(items[activeContainerItemKey].view).length > 0) {
        const activeContainerItem = items[activeContainerItemKey];
        const target = activeContainerItemKey.substring(0, activeContainerItemKey.lastIndexOf('_'));

        const { view: rootView, context } = activeContainerItem;
        const { viewName, viewContext } = this.processRootViewDetails(rootView, activeContainerItem, pageReference);

        if (!viewName) return null;

        const config = {
          meta: rootView,
          options: {
            context,
            pageReference: viewContext || pageReference,
            containerName,
            containerItemID: activeContainerItemKey,
            parentPageReference: pageReference,
            hasForm:
              isAssignmentView ||
              containerName === CONTAINER_NAMES.WORKAREA ||
              containerName === CONTAINER_NAMES.MODAL ||
              viewName === constants.CREATE_DETAILS_VIEW_NAME,
            target
          }
        };

        return createC11nEnv(config).getPConnect;
      }
    }
    return null;
  }

  public static processRootViewDetails(
    rootView: { config?: { context: any; name: any } | undefined },
    containerItem: { context: any },
    pageReference: string
  ) {
    const { config: { context: viewContext, name: viewName } = {} } = rootView;
    const { context: containerContext } = containerItem;
    let resolvedViewName = viewName;
    let resolvedViewContext = viewContext;

    const isAnnotedViewName = AnnotationUtils.isProperty(viewName);
    const isAnnotedViewContext = AnnotationUtils.isProperty(viewContext);

    // resolving annoted view context
    if (isAnnotedViewContext) {
      const viewContextProperty = AnnotationUtils.getPropertyName(viewContext);
      resolvedViewContext = getValue(
        `.${viewContextProperty}`,
        viewContextProperty.startsWith('.') ? pageReference : '',
        containerContext
      );
    }

    if (!resolvedViewContext) {
      resolvedViewContext = pageReference;
    }

    // resolving annoted view name
    if (isAnnotedViewName) {
      const viewNameProperty = AnnotationUtils.getPropertyName(viewName);
      resolvedViewName = getValue(`.${viewNameProperty}`, resolvedViewContext, containerContext);
    }

    /* Special case where context and viewname are dynamic values
      Use case - split for each shape

      Ex - (caseInfo.content.SCRequestWorkQueues[1]):context --> .pyViewName:viewName
    */
    if (isAnnotedViewName && isAnnotedViewContext && resolvedViewName !== '') {
      /* Allow context processor to resolve view and context when both are dynamic */
      resolvedViewName = viewName;
      resolvedViewContext = viewContext;
    }

    return {
      viewName: resolvedViewName,
      viewContext: resolvedViewContext
    };
  }

  static addFlowContainerItem(pConnect: C11nEnv) {
    const containerManager = pConnect.getContainerManager();
    const contextName = pConnect.getContextName(); // here we will get parent context name, as flow container is child of view container
    const caseViewMode = pConnect.getValue('context_data.caseViewMode');

    let key;
    let flowName;

    if (caseViewMode !== 'review') {
      const target = contextName.substring(0, contextName.lastIndexOf('_'));
      const activeContainerItemID = getActiveContainerItemName(target);
      const containerItemData = getContainerItemData(target, activeContainerItemID);

      if (containerItemData) {
        ({ key, flowName } = containerItemData);
      }
    }

    containerManager.addContainerItem({
      semanticURL: '',
      key,
      flowName,
      caseViewMode: 'perform',
      resourceType: 'ASSIGNMENT',
      data: pConnect.getDataObject(contextName)
    });
  }

  static hasContainerItems = (pConnect: C11nEnv) => {
    const contextName = pConnect.getContextName();
    const containerName = pConnect.getContainerName();
    return PCore.getContainerUtils().hasContainerItems(`${contextName}/${containerName}`);
  };

  static hasGetNextWorkLinks = (pConnect: C11nEnv) => pConnect.getValue(constants.CASE_INFO.GETNEXTWORK);

  static getActiveCaseActionName = (pConnect: C11nEnv) => {
    const caseActions = pConnect.getValue(constants.CASE_INFO.CASE_INFO_ACTIONS);
    const activeActionID = pConnect.getValue(constants.CASE_INFO.ACTIVE_ACTION_ID);
    const activeAction = caseActions.find((action: Action) => action.ID === activeActionID);
    return activeAction?.name || '';
  };

  static getFirstCaseActionName = (pConnect: C11nEnv) => {
    const caseActions = pConnect.getValue(constants.CASE_INFO.CASE_INFO_ACTIONS);
    return caseActions[0]?.name || '';
  };

  static hasNotificationMessages = (pConnect: C11nEnv) => !!pConnect.getValue('caseMessages');

  static isCaseWideLocalAction = (pConnect: C11nEnv) => {
    const actionID = pConnect.getValue(constants.CASE_INFO.ACTIVE_ACTION_ID);
    const caseActions: Action[] = pConnect.getValue(constants.CASE_INFO.CASE_INFO_ACTIONS);
    if (caseActions && actionID) {
      const activeAction = caseActions.find((caseAction: Action) => caseAction.ID === actionID);
      return activeAction?.type === 'Case';
    }
    return false;
  };

  static getChildCaseAssignments = (pConnect: C11nEnv): Assignment[] | undefined => {
    const childCases = pConnect.getValue(constants.CASE_INFO.CHILD_ASSIGNMENTS);
    let allAssignments: Assignment[] = [];
    if (childCases && childCases.length > 0) {
      childCases.forEach(({ assignments = [], Name, caseTypeID }: CaseItem) => {
        const childCaseAssignments = assignments.map((assignment: Assignment) => ({
          ...assignment,
          caseName: Name,
          caseTypeID
        }));
        allAssignments = allAssignments.concat(childCaseAssignments);
      });
    }
    return allAssignments;
  };

  static hasAssignments = (pConnect: C11nEnv) => {
    const assignments = pConnect.getValue(constants.CASE_INFO.D_CASE_ASSIGNMENTS_RESULTS);
    const childCasesAssignments = FlowContainer.getChildCaseAssignments(pConnect);

    return !!(
      (Array.isArray(childCasesAssignments) && childCasesAssignments.length > 0) ||
      (Array.isArray(assignments) && assignments.length > 0) ||
      FlowContainer.isCaseWideLocalAction(pConnect)
    );
  };

  static showBanner = (getPConnect: () => C11nEnv) => {
    const pConnect = getPConnect();
    return FlowContainer.hasNotificationMessages(pConnect) || !FlowContainer.hasAssignments(pConnect);
  };

  static showTodo = (pConnect: C11nEnv) => {
    const caseViewMode = pConnect.getValue('context_data.caseViewMode');
    return caseViewMode !== 'perform';
  };

  static isRenderWithToDoWrapper = (getPConnect: () => C11nEnv, options: { showWithToDo?: string | boolean }) => {
    const pConnect: C11nEnv = getPConnect();
    const { showWithToDo } = options;
    return showWithToDo && (!FlowContainer.isCaseWideLocalAction(pConnect) || FlowContainer.showTodo(pConnect));
  };

  static getToDoAssignments = (pConnect: C11nEnv) => {
    const caseActions = pConnect.getValue(constants.CASE_INFO.CASE_INFO_ACTIONS);
    const assignmentLabel = pConnect.getValue(constants.CASE_INFO.ASSIGNMENT_LABEL);
    const assignments = pConnect.getValue(constants.CASE_INFO.D_CASE_ASSIGNMENTS_RESULTS) || [];
    const childCasesAssignments = FlowContainer.getChildCaseAssignments(pConnect) || [];
    let childCasesAssignmentsCopy = JSON.parse(JSON.stringify(childCasesAssignments));

    childCasesAssignmentsCopy = childCasesAssignmentsCopy.map((assignment: Assignment) => {
      assignment.isChild = true;
      return assignment;
    });

    const todoAssignments = [...assignments, ...childCasesAssignmentsCopy];
    let todoAssignmentsCopy: typeof todoAssignments = JSON.parse(JSON.stringify(todoAssignments));

    if (caseActions && !FlowContainer.showTodo(pConnect)) {
      todoAssignmentsCopy = todoAssignmentsCopy.map((assignment: Assignment) => {
        assignment.name = FlowContainer.getActiveCaseActionName(pConnect) || assignment?.name || assignmentLabel;
        return assignment;
      });
    }

    return todoAssignmentsCopy;
  };
}

export default FlowContainer;
